import { useState, useCallback, useRef, useEffect } from 'react'
import { timetableApi } from '@/services/api/timetable'

interface UndoState {
  canUndo: boolean
  remainingSeconds: number
  startUndo: (changeId: string) => void
  triggerUndo: () => Promise<void>
  isReverting: boolean
}

export function useUndo({
  duration = 300000, // 5 minutes
  onUndoSuccess,
  onUndoError,
}: {
  duration?: number
  onUndoSuccess?: () => void
  onUndoError?: (error: string) => void
}): UndoState {
  const [canUndo, setCanUndo] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [isReverting, setIsReverting] = useState(false)
  const [changeId, setChangeId] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef<number | null>(null)

  const clearUndo = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setCanUndo(false)
    setRemainingSeconds(0)
    setChangeId(null)
    startTimeRef.current = null
  }, [])

  const startUndo = useCallback((newChangeId: string) => {
    clearUndo()
    setChangeId(newChangeId)
    setCanUndo(true)
    startTimeRef.current = Date.now()
    
    intervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Date.now() - startTimeRef.current
        const remaining = Math.ceil((duration - elapsed) / 1000)
        
        if (remaining <= 0) {
          clearUndo()
        } else {
          setRemainingSeconds(remaining)
        }
      }
    }, 1000)
  }, [duration, clearUndo])

  const triggerUndo = useCallback(async () => {
    if (!canUndo || !changeId) return
    
    setIsReverting(true)
    try {
      await timetableApi.revertValidity(changeId)
      clearUndo()
      onUndoSuccess?.()
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Rückgängig machen fehlgeschlagen'
      onUndoError?.(errorMsg)
      // Don't clear undo on error - user can retry
    } finally {
      setIsReverting(false)
    }
  }, [canUndo, changeId, clearUndo, onUndoSuccess, onUndoError])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return {
    canUndo,
    remainingSeconds,
    startUndo,
    triggerUndo,
    isReverting,
  }
}
